import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { RequestsService } from "../../../services/requests.service";
import { KeyValueObject } from "../../../models/core/KeyValueObject";
import { DropDownFilterSettings } from "@progress/kendo-angular-dropdowns";
import { DialogRef } from "@progress/kendo-angular-dialog";
import { ReclamationDevicesService } from "../../../services/reclamation-devices.service";
import { RequestDevicesService } from "../../../services/request-devices.service";
import { forkJoin } from "rxjs";
import { ReclamationDeviceDto } from "../../../models/reclamation/reclamation-device.dto";
import { ReclamationDeviceDeviceModelDto } from "../../../models/reclamation/reclamation-device-device-model.dto";
import { RequestClaimDevice } from "../../../models/request/request-claim-device";
import { DeviceModelsService } from "../../../services/device-models.service";
import { DeviceModelDto } from "../../../models/device/device-model-dto";
import { LookupService } from "../../../services/lookup.service";

@Component({
    selector: 'set-problem-resolution',
    templateUrl: './set-problem-resolution.component.html',
    styleUrls: ['./set-problem-resolution.component.scss']
})
export class SetProblemResolutionComponent implements OnInit {

    public dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };

    @Input() requestId: number;
    @Input() claimId: number;
    @Input() resolution: string;
    @Input() reclamationDeviceId: number;

    @Output() onCancel: EventEmitter<any> = new EventEmitter<any>();
    @Output() onContinue: EventEmitter<any> = new EventEmitter<any>();

    reclamationDevices: ReclamationDeviceDto[] = [];
    reclamationDevicesDropdownData: KeyValueObject[] = [];
    reclamationDeviceModels: ReclamationDeviceDeviceModelDto[] = [];
    requestClaimDevice: RequestClaimDevice = null;
    problems: KeyValueObject[] = [];
    deviceModels: DeviceModelDto[] = [];
    deviceModelsDropdownData: KeyValueObject[] = [];
    marketPlacesDropdownData: KeyValueObject[] = [];
    reclamationProductGroups: KeyValueObject[] = [];
    selectedReclamationDeviceId: number;
    reclamationProductGroupId: number;

    loaded: boolean = false;
    isNew: boolean = false;
    dataSaving: boolean = false;

    constructor(
        private dialog: DialogRef,
        private requestsService: RequestsService,
        private reclamationDevicesService: ReclamationDevicesService,
        private requestDevicesService: RequestDevicesService,
        private deviceModelsService: DeviceModelsService,
        private lookupService: LookupService
    ) { }

    ngOnInit(): void {
        this.selectedReclamationDeviceId = this.reclamationDeviceId;

        const deviceMarketPlaces$ = this.lookupService.getData('device-market-places', null);
        const reclamationDevices$ = this.reclamationDevicesService.getAll();
        const reclamationDeviceModels$ = this.reclamationDevicesService.getAllReclamationDeviceModels();
        const claimRequestDevices$ = this.requestDevicesService.getRequestClaimDevices(this.requestId);
        const problems$ = this.requestsService.getRequestProblems(this.requestId);
        const deviceModels$ = this.deviceModelsService.getAll(true);
        const reclamationProductGroups$ = this.lookupService.getData('reclamation-product-groups', null);

        forkJoin([reclamationDevices$, reclamationDeviceModels$, claimRequestDevices$, problems$, deviceModels$, deviceMarketPlaces$])
            .subscribe(([reclamationDevices, reclamationDeviceModels, claimRequestDevices, problems, deviceModels, deviceMarketPlaces]) => {
                forkJoin([reclamationProductGroups$])
                    .subscribe(([reclamationProductGroups]) => {
                        this.reclamationDevices = reclamationDevices;
                        this.reclamationDevicesDropdownData = this.reclamationDevices.map(x => new KeyValueObject(x.reclamationDeviceId, x.name));
                        this.reclamationDeviceModels = reclamationDeviceModels;
                        this.problems = problems.data;
                        this.deviceModels = deviceModels.filter(x => x.parentId != null);
                        this.marketPlacesDropdownData = deviceMarketPlaces;
                        this.reclamationProductGroups = reclamationProductGroups;
                        let reclamationDevice = this.reclamationDevices.find(f => f.reclamationDeviceId === this.reclamationDeviceId);

                        if (reclamationDevice) {
                            this.reclamationProductGroupId = reclamationDevice.reclamationProductGroupId;
                        }

                        claimRequestDevices.forEach(f => {
                            if (f.manufactureDate != null) {
                                f.manufactureDate = new Date(f.manufactureDate);
                            }                  
                        });

                        if (claimRequestDevices.length > 0) {
                            this.requestClaimDevice = claimRequestDevices[0];
                            this.requestClaimDevice.requestId = this.requestId;
                        }
                        else {
                            this.isNew = true;
                            this.requestClaimDevice = new RequestClaimDevice;
                            this.requestClaimDevice.requestId = this.requestId;
                            let reclamationDeviceModel = this.reclamationDeviceModels.find(x => x.reclamationDeviceId == this.reclamationDeviceId);
                            if (reclamationDeviceModel != null) {
                                this.requestClaimDevice.deviceModelId = reclamationDeviceModel.deviceModelId;
                            }
                        }

                        if (!!!this.requestClaimDevice.claimedDeviceQuantity) {
                            this.requestClaimDevice.claimedDeviceQuantity = 1;
                        }

                        this.changeReclamationDevice(this.reclamationDeviceId);
                        this.loaded = true;
                    });
            });
    }

    changeReclamationProductGroupd() {
        this.selectedReclamationDeviceId = null;
    }

    handleReclamationDevicesDropdownOpen() {
        this.reclamationDevicesDropdownData = this.reclamationDevices
            .filter(x => x.reclamationProductGroupId == this.reclamationProductGroupId)
            .map(x => new KeyValueObject(x.reclamationDeviceId, x.name));
    }

    changeReclamationDevice(reclamationDeviceId: number) {
        if (reclamationDeviceId != null) {
            let deviceModelIds = this.reclamationDeviceModels.filter(x =>
                x.reclamationDeviceId == reclamationDeviceId).map(x => x.deviceModelId);

            this.deviceModelsDropdownData = this.deviceModels.filter(x => deviceModelIds.includes(x.id)).map(x =>
                new KeyValueObject(x.id, x.name));

            if (this.deviceModelsDropdownData.length === 0) {
                this.deviceModelsDropdownData = this.deviceModels.map(x => new KeyValueObject(x.id, x.name));
            }

            if (!deviceModelIds.includes(this.requestClaimDevice.deviceModelId)) {
                this.requestClaimDevice.deviceModelId = null;
            }

            if (deviceModelIds.length == 1) {
                this.requestClaimDevice.deviceModelId = deviceModelIds[0];
            }

            this.requestsService.getRequestProblems(this.requestId, reclamationDeviceId).subscribe((resp) => {
                this.problems = resp.data;
                if (!this.problems.some(x => x.id == this.claimId)) {
                    this.claimId = null;
                }
            })
        }
        else {
            this.problems = [];
            this.claimId = null;
        }
    }

    cancel() {
        this.onCancel.emit();
        this.dialog.close();
    }

    continue() {
        this.dataSaving = true;

        const updateReclamation$ = this.reclamationDevicesService.updateReclamationRequestDevice(this.requestId, this.selectedReclamationDeviceId);
        const updateProblem$ = this.requestsService.updateRequestProblemResolution(this.requestId, this.claimId, this.resolution);
        const claimDeviceAction$ = this.isNew
            ? this.requestDevicesService.createRequestClaimDevice(this.requestClaimDevice)
            : this.requestDevicesService.updateRequestClaimDevice(this.requestClaimDevice);

        forkJoin([updateReclamation$, updateProblem$, claimDeviceAction$]).subscribe(() => {
            this.onContinue.emit();
            this.dialog.close();
            this.dataSaving = false;
        });
    }

    get canSave(): boolean {
        return this.claimId && this.resolution != null && this.resolution.trim() != '' && this.serialNumberValid &&
            this.requestClaimDevice.deviceModelId != null && this.selectedReclamationDeviceId != null;
    }

    get serialNumberValid(): boolean {
        if (this.requestClaimDevice.deviceModelId) {
            let deviceModel = this.deviceModels.find(x => this.requestClaimDevice.deviceModelId == x.id);

            if (deviceModel != null) {
                let valid = !deviceModel.isSerialNumberRequired ||
                    (this.requestClaimDevice.serialNumber != null && this.requestClaimDevice.serialNumber.trim() != '');

                return valid;
            }
        }

        return false;
    }
}